import React, { useState, useEffect, useCallback } from "react";
import { Grid, Typography, Pagination, Stack } from "@mui/material";
import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import DateRangeFilter from "../components/common/DateRangeFilter";
import AppCircularProgress from "../components/common/AppCircularProgress";
import LedgerEntryCard from "../components/ledger/LedgerEntryCard";
import useDebounce from "../hooks/useDebounce";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { getLedgerEntries } from "../redux/actions/ledgerEntryActions";
import PageError from "../components/common/PageError";

const LedgerEntriesPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const today = new Date().toISOString().split("T")[0];

  const {
    items: ledgerEntries,
    pagination,
    loading: loadingLedgerEntries,
    error: errorLoadingLedgerEntries,
  } = useAppSelector((state) => state.ledgerEntry);

  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce<string>(searchTerm, 500);
  const [startDate, setStartDate] = useState<string>(today);
  const [endDate, setEndDate] = useState<string>(today);
  const [page, setPage] = useState<number>(1);

  const fetchEntries = useCallback(() => {
    dispatch(
      getLedgerEntries({
        page,
        size: 12,
        search: debouncedSearch,
        startDate,
        endDate,
      }),
    );
  }, [dispatch, page, debouncedSearch, startDate, endDate]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, startDate, endDate]);

  return (
    <PageContainer
      title="General Ledger"
      subtitle="View all double-entry accounting records."
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search transactions or accounts..."
      >
        <DateRangeFilter
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
        />
      </PageToolbar>

      {loadingLedgerEntries ? (
        <AppCircularProgress />
      ) : errorLoadingLedgerEntries ? (
        <PageError error={errorLoadingLedgerEntries} onRetry={fetchEntries} />
      ) : ledgerEntries.length === 0 ? (
        <Typography sx={{ mt: 5, textAlign: "center" }}>
          No ledger entries found for this period.
        </Typography>
      ) : (
        <>
          <Grid container spacing={3}>
            {ledgerEntries.map((entry) => (
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={entry.id}>
                <LedgerEntryCard entry={entry} />
              </Grid>
            ))}
          </Grid>

          {pagination && pagination.totalPages! > 1 && (
            <Stack sx={{ alignItems: "flex-end", mt: 3 }}>
              <Pagination
                count={pagination.totalPages}
                page={page}
                onChange={(_e, val) => setPage(val)}
                color="primary"
              />
            </Stack>
          )}
        </>
      )}
    </PageContainer>
  );
};

export default LedgerEntriesPage;
